import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Check,
  Star,
  TrendingUp,
  Users,
  BarChart3,
  MapPin,
  Shield,
  Zap,
  Crown,
  Building,
  CreditCard,
  ChevronRight,
  Sparkles,
  Eye,
  MessageCircle,
  Heart,
  Calculator,
  Plus,
  Minus,
  Lock,
  AlertCircle,
  EyeOff,
  Loader2
} from 'lucide-react';
import { MembershipPlan } from '../types';
// import { businessMemberAPI } from '../services/api';

import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_CONFIG } from '../config/googleMaps';
import { orgAPI, paymentAPI } from '../services/api';
import CheckoutForm from './PaymentModal';


// one shared loader
const loader = new Loader({
  apiKey: GOOGLE_MAPS_CONFIG.apiKey,
  libraries: ["places", "marker"],
});

interface BusinessMembershipSignupProps {
  onClose: () => void;
  onSignupComplete: (businessData: any) => Promise<any> | void;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
}

const BusinessMembershipSignup: React.FC<BusinessMembershipSignupProps> = ({
  onClose,
  onSignupComplete
}) => {
  const [currentStep, setCurrentStep] = useState<'plans' | 'details' | 'locations' | 'payment'>('plans');
  const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null);
  const [locationCount, setLocationCount] = useState(1);
  const [isSubmittingSignup, setIsSubmittingSignup] = useState(false);
  const [businessData, setBusinessData] = useState({
    orgName: '',
    ownerName: '',
    email: '',
    phone: '',
    website: '',
    description: '',
    category: '',
    password: '',
    confirmPassword: '',
    location: [{ name: '', address: '', latitude: 0, longitude: 0 }]
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlan[]>([]);
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Real-time password validation function
  const validatePassword = (field: 'password' | 'confirmPassword', value: string) => {
    const newErrors = { ...errors };

    if (field === 'password') {
      // Password validation
      if (!value) {
        newErrors.password = 'Password is required';
      } else if (value.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else {
        delete newErrors.password; // Clear error if valid
      }

      // If confirmPassword exists, re-validate it
      if (businessData.confirmPassword) {
        if (value !== businessData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          delete newErrors.confirmPassword;
        }
      }
    } else if (field === 'confirmPassword') {
      // Confirm password validation
      if (!value) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (businessData.password && value !== businessData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete newErrors.confirmPassword; // Clear error if valid
      }
    }

    setErrors(newErrors);
  };

  const handlePasswordChange = (field: 'password' | 'confirmPassword', value: string) => {
    // Update the state
    setBusinessData({ ...businessData, [field]: value });

    // Validate in real-time
    validatePassword(field, value);
  };

  const inputRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    let listeners: google.maps.MapsEventListener[] = [];

    loader.load().then((google) => {
      businessData.location.forEach((_, idx) => {
        const el = inputRef.current[idx];
        if (!el) return;

        const ac = new google.maps.places.Autocomplete(el, {
          fields: ['place_id', 'formatted_address', 'geometry'],
          types: ['address'],
        });

        const listener = ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          const loc = place.geometry?.location;
          if (!loc) return;
        });

        listeners.push(listener);
      });
    });

    return () => {
      listeners.forEach((l) => l.remove());
    };
  }, [businessData.location.length, currentStep]);

  const areLocationsComplete = Array.isArray(businessData.location) &&
    businessData.location.every((loc) =>
      (loc?.name?.trim?.() ?? '') !== '' &&
      (loc?.address?.trim?.() ?? '') !== ''
    );

  // get membership plan from database
  useEffect(() => {
    const fetchMembershipPlans = async () => {
      try {
        const plans = await paymentAPI.getMembershipPlan();
        setMembershipPlans(plans);
      } catch (error: any) {
        console.error('Error fetching membership plans:', error);
      }
    };
    fetchMembershipPlans();
  }, []);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatLocationLimit = (plan: MembershipPlan) => {
    if (plan.maxLocations === 'unlimited' || plan.maxLocations === null) {
      return 'Unlimited locations';
    }

    const maxLocations = Number(plan.maxLocations);
    return `${maxLocations} location${maxLocations === 1 ? '' : 's'} max`;
  };

  const recommendedPlanName = (planName: string) =>
    planName === 'Starter' ? 'Professional' : 'Enterprise';

  const calculateMonthlyFee = (plan: MembershipPlan, locations: number) => {
    if (plan.maxLocations === 'unlimited' || plan.maxLocations === null) {
      return plan.pricePerLocation * locations;
    }
    return plan.pricePerLocation * Math.min(locations, plan.maxLocations);
  };

  const renderPlansStep = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Choose your Piezit membership
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Select the tier that fits your business today. Pricing stays per location,
          so your monthly total scales with the number of locations you manage.
        </p>
      </div>

      {/* Value Proposition Stats */}
      <div className="grid md:grid-cols-3 gap-4 py-6 border-t border-b border-gray-200">
        <div className="rounded-lg bg-primary-50 p-4 text-center">
          <div className="text-sm font-semibold text-primary-700 mb-1">Profile visibility</div>
          <div className="text-sm text-gray-600">Show customers where to find and review you.</div>
        </div>
        <div className="rounded-lg bg-primary-50 p-4 text-center">
          <div className="text-sm font-semibold text-primary-700 mb-1">Review engagement</div>
          <div className="text-sm text-gray-600">Respond to feedback and keep the conversation active.</div>
        </div>
        <div className="rounded-lg bg-primary-50 p-4 text-center">
          <div className="text-sm font-semibold text-primary-700 mb-1">Analytics by location</div>
          <div className="text-sm text-gray-600">Track performance as your footprint grows.</div>
        </div>
      </div>

      {/* Location Counter */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="md:flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">How many locations do you have?</h3>
            <p className="text-gray-600">We’ll estimate your total using per-location pricing.</p>
          </div>
          <div className="flex items-center justify-center pt-3 md:pt-0 space-x-4">
            <button
              onClick={() => setLocationCount(Math.max(1, locationCount - 1))}
              className="p-2 border border-gray-300 rounded-lg hover:bg-white transition-colors duration-200"
              disabled={locationCount <= 1}
            >
              <Minus className="w-4 h-4" />
            </button>
            <div className="text-2xl font-bold text-gray-900 min-w-[3rem] text-center">
              {locationCount}
            </div>
            <button
              onClick={() => setLocationCount(locationCount + 1)}
              className="p-2 border border-gray-300 rounded-lg hover:bg-white transition-colors duration-200"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Membership Plans */}
      <div className="grid md:grid-cols-3 gap-6">
        {membershipPlans.map((plan) => {
          const monthlyFee = calculateMonthlyFee(plan, locationCount);
          const isSelected = selectedPlan?.id === plan.id;
          const maxLocations = Number(plan.maxLocations);
          const canAccommodate =
            plan.maxLocations === 'unlimited' ||
            plan.maxLocations === null ||
            locationCount <= maxLocations;

          return (
            <div
              key={plan.id}
              className={`relative flex min-h-full flex-col border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ${isSelected
                ? 'border-primary-500 bg-primary-50 shadow-lg'
                : canAccommodate
                  ? 'border-gray-200 hover:border-primary-300 hover:shadow-md'
                  : 'border-gray-200 opacity-50 cursor-not-allowed'
                }`}
              onClick={() => canAccommodate && setSelectedPlan(plan)}
            >
              {plan.popular ? (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <Crown className="w-4 h-4" />
                    <span>Most Popular</span>
                  </span>
                </div>
              ) : ('')}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-gray-900">
                    {formatCurrency(plan.pricePerLocation)}
                  </span>
                  <span className="text-base text-gray-600 font-normal">/location/mo</span>
                </div>
                <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  {formatLocationLimit(plan)}
                </div>
                <p className="text-sm text-gray-500 mt-2">{plan.description}</p>
              </div>

              <div className="mb-5 rounded-lg bg-white p-3 text-center shadow-sm">
                <p className="text-xs uppercase tracking-wide text-gray-500">Estimated total</p>
                <p className="text-lg font-semibold text-gray-900">
                  {canAccommodate ? formatCurrency(monthlyFee) : 'Not available'}
                  {canAccommodate && <span className="text-sm font-normal text-gray-600">/mo</span>}
                </p>
                <p className="text-xs text-gray-500">
                  Based on {locationCount} location{locationCount === 1 ? '' : 's'}
                </p>
              </div>

              <div className="flex-1 space-y-3">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {!canAccommodate && (
                <div className="absolute inset-0 bg-white bg-opacity-80 rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Max {maxLocations} location{maxLocations === 1 ? '' : 's'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Consider {recommendedPlanName(plan.name)} plan
                    </p>
                  </div>
                </div>
              )}

              {isSelected && (
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Continue Button */}
      <div className="flex justify-center pt-6">
        <button
          onClick={() => selectedPlan && setCurrentStep('details')}
          disabled={!selectedPlan}
          className="px-8 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
        >
          <span>Continue with {selectedPlan?.name}</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Information</h2>
        <p className="text-gray-600">Tell us about your business to create your profile</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Name *
          </label>
          <input
            type="text"
            value={businessData.orgName}
            onChange={(e) => setBusinessData({ ...businessData, orgName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Your business name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Owner/Manager Name *
          </label>
          <input
            type="text"
            value={businessData.ownerName}
            onChange={(e) => setBusinessData({ ...businessData, ownerName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Your full name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            value={businessData.email}
            onChange={(e) => setBusinessData({ ...businessData, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="business@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            value={businessData.phone}
            onChange={(e) => setBusinessData({ ...businessData, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={businessData.password}
              onChange={(e) => handlePasswordChange('password', e.target.value)}
              className={`input-field pl-10 pr-10`}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <div className="flex items-center space-x-1 mt-1 text-red-600">
              <AlertCircle className="w-3 h-3" />
              <span className="text-xs">{errors.password}</span>
            </div>
          )}
        </div>

        {/* {isSignUp && ( */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={businessData.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              className={`input-field pl-10 pr-10`}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <div className="flex items-center space-x-1 mt-1 text-red-600">
              <AlertCircle className="w-3 h-3" />
              <span className="text-xs">{errors.confirmPassword}</span>
            </div>
          )}
        </div>
        {/* )} */}



        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Website
          </label>
          <input
            type="url"
            value={businessData.website}
            onChange={(e) => setBusinessData({ ...businessData, website: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://www.yourbusiness.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Category *
          </label>
          <select
            value={businessData.category}
            onChange={(e) => setBusinessData({ ...businessData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select a category</option>
            <option value="restaurant">Restaurant</option>
            <option value="retail">Retail Store</option>
            <option value="service">Service Business</option>
            <option value="healthcare">Healthcare</option>
            <option value="automotive">Automotive</option>
            <option value="beauty">Beauty & Wellness</option>
            <option value="professional">Professional Services</option>
            <option value="entertainment">Entertainment</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Description *
        </label>
        <textarea
          value={businessData.description}
          onChange={(e) => setBusinessData({ ...businessData, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Describe your business, what makes it special, and what customers can expect..."
        />
      </div>

      <div className="flex justify-between pt-6">
        <button
          onClick={() => setCurrentStep('plans')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
        >
          Back
        </button>

        <button
          onClick={() => setCurrentStep('locations')}
          disabled={!businessData.orgName || !businessData.ownerName || !businessData.email || !businessData.phone || !businessData.category || !businessData.description || !businessData.password}
          className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
        >
          <span>Continue</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderLocationsStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Add Your Locations</h2>
        <p className="text-gray-600">Add {locationCount} location{locationCount > 1 ? 's' : ''} to complete your profile</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Location Setup</h4>
            <p className="text-sm text-blue-700 mt-1">
              For this demo, we'll collect your location information and set up your profiles.
              In the full version, you'll be able to add detailed addresses, hours, and photos for each location.
            </p>
          </div>
        </div>
      </div>

      {/* Location placeholders */}
      <div className="space-y-4">
        {Array.from({ length: locationCount }, (_, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">
              Location {index + 1} {index === 0 && '(Primary)'}
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                onChange={(e) => {
                  const updated = [...businessData.location];
                  updated[index] = { ...updated[index], name: e.target.value };
                  setBusinessData({ ...businessData, location: updated });
                }}
                placeholder="Location name (optional)"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <input
                type="text"
                ref={el => { inputRef.current[index] = el; }}
                onChange={(e) => {
                  const updated = [...businessData.location];
                  updated[index] = { ...updated[index], address: e.target.value };
                  setBusinessData({ ...businessData, location: updated });
                }}
                placeholder="Full address"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between pt-6 space-x-2">
        <button
          onClick={() => setCurrentStep('details')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
        >
          Back
        </button>

        <button
          onClick={async () => {
            if (!areLocationsComplete) return; // or set an error message
            if (isSubmittingSignup) return;

            const memberData = {
              ...businessData,
              membershipPlan: selectedPlan,
              locationCount
            };

            try {
              setIsSubmittingSignup(true);
              const result = await onSignupComplete(memberData);

              // If the parent returns a backend error payload instead of throwing, handle it too.
              const backendError = (result as any)?.error || (result as any)?.message;
              if (backendError) {
                alert(String(backendError));
                return; // stay on locations step
              }

              setCurrentStep('payment');
            } catch (err: any) {
              console.error('Business signup failed:', err);
              alert(err?.message || 'Business signup failed. Please try again.');
              // stay on locations step
            } finally {
              setIsSubmittingSignup(false);
            }
          }}

          disabled={!areLocationsComplete || isSubmittingSignup}
          className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
        >
          <span>{isSubmittingSignup ? 'Creating account…' : 'Continue to Payment'}</span>
          {isSubmittingSignup ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  const renderPaymentStep = () => {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Subscription</h2>
          <p className="text-gray-600">Secure payment processing powered by industry-leading encryption</p>
        </div>
        <CheckoutForm
          selectedPlanId={selectedPlan?.id!}
          orgEmail={businessData.email}
        />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Piezit Business</h1>
              <p className="text-sm text-gray-600">Step {
                currentStep === 'plans' ? '1' :
                  currentStep === 'details' ? '2' :
                    currentStep === 'locations' ? '3' : '4'
              } of 4</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {currentStep === 'plans' && renderPlansStep()}
          {currentStep === 'details' && renderDetailsStep()}
          {currentStep === 'locations' && renderLocationsStep()}
          {currentStep === 'payment' && renderPaymentStep()}
        </div>
      </div>
    </div>
  );
};

export default BusinessMembershipSignup;
